// openDotaPickInsight.js
// ---------------------------------------------------------------------------
// Extracts the "why" behind one hero pick/ban in one historical pro match —
// what happened around it, using only OpenDota's public match JSON (no
// replay file needed). Feeds Scout Simulator's per-sample-match AI insight.
//
// Pure data extraction, no AI/network here. All facts are computed
// deterministically; the caller decides how to phrase them.
//
// Validated by hand against match_id 8227857210 (Team Liquid vs Tundra
// Esports, 2025-03-24) before writing this — see session notes. Key finding:
// match.draft_timings[].hero_id uses a DIFFERENT hero_id encoding than
// match.picks_bans[]/match.players[] (near-zero overlap in ID sets) — do not
// use draft_timings for hero identity. picks_bans[] alone has everything
// needed: order (0-indexed, so Scout Simulator's 1-indexed slot = order+1),
// is_pick, team (0=radiant, 1=dire), hero_id (matches players[].hero_id).
// ---------------------------------------------------------------------------

const FIGHT_WINDOW_S = 180; // how far past a teamfight's end to look for a follow-up objective
const LANE_CHECK_MINUTE = 5; // index into gold_t/xp_t for the laning-phase snapshot

/**
 * @param {object} match  raw OpenDota /matches/:id response
 * @param {number} slot   Scout Simulator's 1-indexed draft slot (1..24)
 * @returns {null | { heroId:number, side:'radiant'|'dire', isPick:boolean }}
 *   null if this match's picks_bans doesn't have that many entries (e.g. a
 *   shorter draft format) or the data is missing.
 */
export function resolveSlotPick(match, slot) {
  const pb = match && Array.isArray(match.picks_bans) ? match.picks_bans : null;
  if (!pb) return null;
  const entry = pb.find((e) => e.order === slot - 1);
  if (!entry) return null;
  return {
    heroId: entry.hero_id,
    side: entry.team === 0 ? 'radiant' : 'dire',
    isPick: !!entry.is_pick,
  };
}

function findPlayer(match, heroId) {
  return (match.players || []).find((p) => p.hero_id === heroId) || null;
}

function playerIndexInTeamfight(match, player) {
  const order = (match.players || []).map((p) => p.player_slot);
  const idx = order.indexOf(player.player_slot);
  return idx === -1 ? null : idx;
}

/**
 * Net gold/xp advantage for this hero's side at the 5-minute mark. Only
 * meaningful for picks that were on the board from the start of laning —
 * still computed for bans/late picks, but callers should treat lane data
 * as less relevant for those.
 */
function extractLaneResult(player) {
  const g = player.gold_t;
  const x = player.xp_t;
  if (!Array.isArray(g) || g.length <= LANE_CHECK_MINUTE) return null;
  return {
    goldAt5: g[LANE_CHECK_MINUTE],
    xpAt5: Array.isArray(x) ? x[LANE_CHECK_MINUTE] : null,
  };
}

/**
 * The first teamfight this hero actually participated in (nonzero deaths,
 * damage, or gold_delta for their slot), with the fight's outcome inferred
 * from each side's summed gold_delta. Deterministic tie-break: equal
 * gold_delta counts as no clear winner.
 */
function extractFirstFightInvolvement(match, player) {
  const idx = playerIndexInTeamfight(match, player);
  if (idx == null) return null;
  const teamfights = match.teamfights || [];
  const slots = (match.players || []).map((p) => p.player_slot);

  for (const tf of teamfights) {
    const mine = tf.players && tf.players[idx];
    if (!mine) continue;
    const involved = (mine.deaths || 0) > 0 || (mine.damage || 0) > 0 || (mine.gold_delta || 0) !== 0;
    if (!involved) continue;

    let radiantGold = 0;
    let direGold = 0;
    (tf.players || []).forEach((p, i) => {
      const delta = p.gold_delta || 0;
      if (slots[i] < 128) radiantGold += delta;
      else direGold += delta;
    });

    let winner = null;
    if (radiantGold > direGold) winner = 'radiant';
    else if (direGold > radiantGold) winner = 'dire';

    return {
      startS: tf.start,
      endS: tf.end,
      deaths: tf.deaths,
      winner, // 'radiant' | 'dire' | null (even trade)
      heroWon: winner == null ? null : winner === (player.player_slot < 128 ? 'radiant' : 'dire'),
    };
  }
  return null;
}

/**
 * The first building/Roshan kill within FIGHT_WINDOW_S seconds after a
 * given teamfight ended, on the winning side. Answers "did the fight get
 * converted into an objective."
 */
function extractFollowUpObjective(match, fight) {
  if (!fight || !fight.winner) return null;
  const objectives = match.objectives || [];
  const hit = objectives.find((o) => {
    if (o.time < fight.endS || o.time > fight.endS + FIGHT_WINDOW_S) return false;
    return o.type === 'building_kill' || o.type === 'CHAT_MESSAGE_ROSHAN_KILL';
  });
  if (!hit) return null;
  return {
    type: hit.type === 'CHAT_MESSAGE_ROSHAN_KILL' ? 'roshan' : 'tower',
    time: hit.time,
    key: hit.key || null,
  };
}

/**
 * Full fact bundle for "why did this hero matter in this specific game."
 * Every field can independently be null — callers must handle partial data;
 * OpenDota's parse completeness varies match to match.
 *
 * @param {object} match  raw OpenDota /matches/:id response
 * @param {number} slot   Scout Simulator's 1-indexed draft slot
 * @returns {null | {
 *   heroId:number, side:'radiant'|'dire', isPick:boolean,
 *   lane: null | { goldAt5:number, xpAt5:number|null },
 *   firstFight: null | { startS:number, endS:number, deaths:number, winner:string|null, heroWon:boolean|null },
 *   followUpObjective: null | { type:'tower'|'roshan', time:number, key:string|null }
 * }}
 */
export function extractPickInsight(match, slot) {
  const resolved = resolveSlotPick(match, slot);
  if (!resolved) return null;

  const player = findPlayer(match, resolved.heroId);
  if (!player) {
    // Ban with no corresponding player (expected — banned heroes aren't played),
    // or a data gap. Either way, only the identity facts are available.
    return { ...resolved, lane: null, firstFight: null, followUpObjective: null };
  }

  const lane = extractLaneResult(player);
  const firstFight = extractFirstFightInvolvement(match, player);
  const followUpObjective = extractFollowUpObjective(match, firstFight);

  return { ...resolved, lane, firstFight, followUpObjective };
}
